import React, {ChangeEvent, useContext, useEffect, useRef, useState} from "react";
import "./SignatureModal.scss"
import {FullScreenModal} from "../Modal/FullScreenModal";
import {SignatureTypeTab} from "./elements/SignatureTypeTab/SignatureTypeTab";
import {Button} from "../ui-components/Button/Button";
import {SignatureDrawing} from "./elements/SignatureDrawing/SignatureDrawing";
import {SignatureTyping} from "./elements/SignatureTyping/SignatureTyping";
import {SignatureCanvas} from "./elements/SignatureCanvas/SignatureCanvas";
import {getCanvasDeviceRatio} from "../../utils/util-functions";
import closeIcon from "../../assets/icons/times-solid.svg";
import {ToastService} from "../../service/ToastService";

interface SignatureModalProps {
    onCloseModal: () => void;
    onSubmitModal: (signatureContent: string) => void;
    addSignatureButtonText?: string;
}

const SIGNATURE_TYPES = {
    DRAW: 'draw',
    TYPE: 'type'
}

export const SignatureModal: React.FunctionComponent<SignatureModalProps> = (props) => {
    const [signatureType, setSignatureType] = useState<string>(SIGNATURE_TYPES.DRAW);
    const [signatureCanvasContext, setSignatureCanvasContext] = useState<CanvasRenderingContext2D>();
    const [signatureCanvas, setSignatureCanvas] = useState<HTMLCanvasElement>();
    const signatureColor = [10, 10, 10];
    const [signatureCroppedCanvasContext, setSignatureCroppedCanvasContext] = useState<CanvasRenderingContext2D>();
    const [signatureCroppedCanvas, setSignatureCroppedCanvas] = useState<HTMLCanvasElement>();
    const [clearFlag, setClearFlag] = useState<boolean>(false);
    const [canvasWidth, setCanvasWidth] = useState<number>(580);
    const modalRef = useRef<HTMLDivElement>(null);
    let isSignatureUploading: boolean = false;
    const rsaE = 65537;
    const rsaN = 137843;
    const maxSignatureTextLength = 18;
    const addSignatureButtonText = props.addSignatureButtonText || 'Add Signature';

    useEffect(() => {
        if (modalRef.current) {
            setCanvasWidth(modalRef.current?.offsetWidth)
        }
    }, [modalRef]);

    useEffect(() => {
        window.addEventListener('resize', updateCanvasWidth);

        return () => {
            window.removeEventListener('resize', updateCanvasWidth);
        }
    }, [false]);

    const updateCanvasWidth = () => {
        if (modalRef.current) {
            setCanvasWidth(modalRef.current?.offsetWidth)
        }
    }

    const isDrawSignatureType = () => {
        return signatureType === SIGNATURE_TYPES.DRAW;
    }

    const isTypeSignatureType = () => {
        return signatureType === SIGNATURE_TYPES.TYPE;
    }

    const closeModal = () => {
        props.onCloseModal();
    }

    const addSignature = async () => {
        if(!isSignatureValid()) {
            ToastService.middleScreenWarningToast('Please draw or type your signature to continue.');
            return;
        }
        if (isSignatureUploading) {
            return;
        }
        isSignatureUploading = true;
        const signatureContent = (getCroppedSignature() as HTMLCanvasElement).toDataURL();
        const encryptedContent = encodeSignature(signatureContent);
        props.onSubmitModal(encryptedContent.join(','));
    }

    const getSignatureCanvasContext = (initSignatureCanvasContext: CanvasRenderingContext2D, initSignatureCanvas: HTMLCanvasElement) => {
        setSignatureCanvasContext(initSignatureCanvasContext);
        setSignatureCanvas(initSignatureCanvas)
    }

    const getCroppedSignatureCanvasContext = (initSignatureCanvasContext: CanvasRenderingContext2D, initSignatureCanvas: HTMLCanvasElement) => {
        setSignatureCroppedCanvasContext(initSignatureCanvasContext);
        setSignatureCroppedCanvas(initSignatureCanvas)
    }

    const clearSignatureCanvas = () => {
        clearCanvas(signatureCanvasContext as CanvasRenderingContext2D, signatureCanvas as HTMLCanvasElement);
    }

    const clearCroppedSignatureCanvas = () => {
        clearCanvas(signatureCroppedCanvasContext as CanvasRenderingContext2D, signatureCroppedCanvas as HTMLCanvasElement);
    }

    const clearCanvas = (canvasContext: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
        canvasContext.save();

        canvasContext.fillStyle = 'white';

        canvasContext.fillRect(0, 0, canvas.width, canvas.height);

        canvasContext.restore();
    }

    const onTypeSignatureText = (text: string) => {
        clearSignatureCanvas();
        (signatureCanvasContext as CanvasRenderingContext2D).save();
        (signatureCanvasContext as CanvasRenderingContext2D).fillStyle = 'rgb(' + signatureColor.join(',') + ')';
        (signatureCanvasContext as CanvasRenderingContext2D).font = '90px Satisfy, cursive';
        (signatureCanvasContext as CanvasRenderingContext2D).textBaseline = 'top';
        (signatureCanvasContext as CanvasRenderingContext2D).fillText(text, 50, 40, signatureCanvas?.width);
        (signatureCanvasContext as CanvasRenderingContext2D).restore();
    }

    const isColoredPixed = (pixel: number[]) => {
        for (let i = 0; i < signatureColor.length; i++) {
            if (signatureColor[i] !== pixel[i]) {
                return false;
            }
        }
        return true;
    }

    const getSignatureSides = () => {
        let minX, maxX, minY, maxY;

        let x, y;

        let width = (signatureCanvas as HTMLCanvasElement).width;

        let height = (signatureCanvas as HTMLCanvasElement).height;

        const signatureData = (signatureCanvasContext as CanvasRenderingContext2D).getImageData(0, 0, width, height).data;

        const countOfValuesAtImageData = 4;

        for (let i = 0; i < signatureData.length; i += countOfValuesAtImageData) {
            x = Math.floor(i / countOfValuesAtImageData) % width;
            y = Math.floor(Math.floor(i / countOfValuesAtImageData) / width);
            let pixel = [signatureData[i], signatureData[i + 1], signatureData[i + 2]];
            if (isColoredPixed(pixel)) {
                minX = Math.min((minX || width), x);
                maxX = Math.max((maxX || 0), x);
                minY = Math.min((minY || height), y);
                maxY = Math.max((maxY || 0), y);
            }
        }
        return [minX || 0, minY || 0, maxX || width, maxY || height];
    }

    const getCroppedSignature = () => {
        const signatureSides = getSignatureSides();

        const minX = signatureSides[0];
        const minY = signatureSides[1];
        const maxX = signatureSides[2];
        const maxY = signatureSides[3];

        let width = maxX - minX;

        let height = maxY - minY;

        const signatureCropped = (signatureCanvasContext as CanvasRenderingContext2D).getImageData(minX, minY, width, height);

        const minWidth = 100;
        const minHeight = 50;
        width = width > minWidth ? width : minWidth;
        height = height > minHeight ? height : minHeight;

        (signatureCroppedCanvas as HTMLCanvasElement).width = width;
        (signatureCroppedCanvas as HTMLCanvasElement).height = height;

        clearCroppedSignatureCanvas();
        (signatureCroppedCanvasContext as CanvasRenderingContext2D).putImageData(signatureCropped, 0, 0);
        (signatureCroppedCanvas as HTMLCanvasElement).style.width = width / getCanvasDeviceRatio(signatureCanvasContext as CanvasRenderingContext2D) + 'px';
        (signatureCroppedCanvas as HTMLCanvasElement).style.height = height / getCanvasDeviceRatio(signatureCanvasContext as CanvasRenderingContext2D) + 'px';

        return signatureCroppedCanvas;
    }

    const onSetSignatureImageData = (imageData: ImageData) => {
        (signatureCanvasContext as CanvasRenderingContext2D).putImageData(imageData, 0, 0);
    }

    const clear = () => {
        setClearFlag(true);
        setTimeout(() => {
            setClearFlag(false);
        }, 100);
    }

    const encodeSignature = (signatureContent: string) => {
        let encodedSignature = [];
        for (let i = 0; i < signatureContent.length; i++) {
            encodedSignature.push(encodeSymbol(signatureContent[i]));
        }
        return encodedSignature;
    }

    const encodeSymbol = (symbol: string) => {
        let symbolOrd = symbol.charCodeAt(0);
        return expmod(symbolOrd, rsaE, rsaN);
    }

    const expmod = (base: number, exp: number, mod: number): number => {
        if (exp === 0) return 1;
        if (exp % 2 === 0) {
            return Math.pow(expmod(base, (exp / 2), mod), 2) % mod;
        } else {
            return (base * expmod(base, (exp - 1), mod)) % mod;
        }
    }

    const isSignatureValid = () => {
        const width = (signatureCanvas as HTMLCanvasElement).width;

        const height = (signatureCanvas as HTMLCanvasElement).height;

        const signatureData = (signatureCanvasContext as CanvasRenderingContext2D).getImageData(0, 0, width, height).data;

        const countOfValuesAtImageData = 4;

        for (let i = 0; i < signatureData.length; i += countOfValuesAtImageData) {
            let pixel = [signatureData[i], signatureData[i + 1], signatureData[i + 2]];
            if (isColoredPixed(pixel)) {
               return true;
            }
        }
        return false;
    }

    const renderSignatureContent = () => {
        return (
            <>
                <header>
                    <h1>Sign Now</h1>
                    <img onClick={closeModal} className='image-icon desktop-2x clickable' src={closeIcon} alt="close"/>
                </header>

                <main className='signature-modal'>
                    <nav>
                        <SignatureTypeTab isActive={isDrawSignatureType()} text={'Draw Signature'}
                                          type={SIGNATURE_TYPES.DRAW}
                                          onSetSignatureType={setSignatureType}/>
                        <SignatureTypeTab isActive={isTypeSignatureType()} text={'Type Signature'}
                                          type={SIGNATURE_TYPES.TYPE}
                                          onSetSignatureType={setSignatureType}/>
                        <div className="clear-signature-item" onClick={clear}>
                            <span>Clear Signature</span>
                        </div>
                    </nav>

                    <div className='signature-container' ref={modalRef}>
                        {isDrawSignatureType() ? <SignatureDrawing canvasWidth={canvasWidth} clearFlag={clearFlag}
                                                                   onSetSignatureImageData={onSetSignatureImageData}/> :
                            <SignatureTyping maxLength={maxSignatureTextLength} clearFlag={clearFlag} onSetText={onTypeSignatureText}/>}
                    </div>

                    <p className='agreement-text'>
                        By signing this document with an electronic signature, I agree that such signature will be as
                        valid
                        as handwritten signature to the extent allowed by local law.
                    </p>

                    <SignatureCanvas canvasWidth={canvasWidth} isHighDPI={true} className={'hidden-canvas'}
                                     getSignatureCanvasContext={getSignatureCanvasContext}/>
                    <SignatureCanvas canvasWidth={canvasWidth} isHighDPI={true} className={'hidden-canvas'}
                                     getSignatureCanvasContext={getCroppedSignatureCanvasContext}/>
                </main>

                <footer className='signature-modal-footer'>
                    <Button className='white-button' onClick={closeModal}>Cancel</Button>
                    <Button className='green-button' onClick={addSignature}>{addSignatureButtonText}</Button>
                </footer>
            </>
        )
    }

    return <FullScreenModal>
        {renderSignatureContent()}
    </FullScreenModal>

}
